import type React from "react";
import { Group, Stack, Text } from "@mantine/core";
import { Card } from "@xplatform/design-system";
import { IconSparkles } from "@tabler/icons-react";
import type { SearchHit } from "@models/search";
import { scoreBadge } from "@utils/score";
import { metadataFields } from "@utils/fields";
import FieldList from "./FieldList";
import ScoreRing from "./ScoreRing";

interface ResultCardProps {
  hit: SearchHit;
  /** 1-based position in the result list. */
  rank: number;
}

/** One technical-manual or wiring-diagram hit: title, score, metadata as fields, reason. */
const ResultCard: React.FC<ResultCardProps> = ({ hit, rank }) => {
  const score = scoreBadge(hit);
  const scoreValue = hit.scoreBasis === "exact_match" ? 100 : hit.similarity == null ? null : hit.similarity * 100;
  const fields = metadataFields(hit.metadata ?? {}, ["text_hash"]);

  return (
    <Card withBorder radius="md" padding="md" shadow="xs">
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
        <Group wrap="nowrap" align="flex-start" gap="sm">
          <Text size="sm" fw={700} c="dimmed" w={24} ta="right" mt={2}>
            {rank}
          </Text>
          <Stack gap={2}>
            <Text fw={600}>{hit.title}</Text>
            {hit.sources && hit.sources.length > 0 && (
              <Text size="xs" c="dimmed">
                Found by {hit.sources.join(", ")}
              </Text>
            )}
          </Stack>
        </Group>
        <ScoreRing value={scoreValue} label={score.label} color={score.color} hint={score.hint} />
      </Group>

      {(fields.length > 0 || hit.reason) && (
        <Stack gap="xs" mt="sm" pl={36}>
          <FieldList fields={fields} />
          {hit.reason && (
            <Group gap={6} wrap="nowrap" align="flex-start">
              <IconSparkles size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
              <Text size="sm" fs="italic" c="dimmed">
                {hit.reason}
              </Text>
            </Group>
          )}
        </Stack>
      )}
    </Card>
  );
};

export default ResultCard;
